import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ExternalLink, Trash2 } from "lucide-react";
import { useState } from "react";

import { CcoAccessCards } from "~/components/admin/cco-access-cards";
import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { DraftReviewCard } from "~/components/admin/draft-review-card";
import { FaqEditor } from "~/components/admin/faq-editor";
import { ImageUploadField } from "~/components/admin/image-upload-field";
import { PageChangeHistory } from "~/components/admin/page-change-history";
import { ReverseLookupCard } from "~/components/admin/reverse-lookup-card";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  approveCcoFn,
  archiveCcoFn,
  ccoQueryKeys,
  type CihCcoRow,
  cihCcoQueryOptions,
  createCcoDraftFn,
  deleteCihCcoFn,
  getCcoCoachMembershipCountFn,
  promoteCcoDraftFn,
  publishCcoFn,
  restoreCcoFn,
  returnCcoToDraftFn,
  updateCihCcoFn,
} from "~/lib/functions/cih/ccos";
import { getCtpsByCcoFn, type CihCtpListRow } from "~/lib/functions/cih/ctps";
import {
  getFrameworksByCcoFn,
  type FrameworkListRow,
} from "~/lib/functions/cih/frameworks";
import {
  discardDraftAsStaffFn,
  pageChangeQueryKeys,
} from "~/lib/functions/organization-pages";
import { CCOType } from "~/lib/schemas/db-enums";

export const Route = createFileRoute("/admin/cih/ccos/$ccoId")({
  staticData: { title: "Edit Organization" },
  loader: async ({ context, params }) => {
    const [data, ctps, frameworks, membershipCount] = await Promise.all([
      context.queryClient.ensureQueryData(cihCcoQueryOptions(params.ccoId)),
      getCtpsByCcoFn({ data: { ccoId: params.ccoId } }),
      getFrameworksByCcoFn({ data: { ccoId: params.ccoId } }),
      getCcoCoachMembershipCountFn({ data: { ccoId: params.ccoId } }),
    ]);
    return {
      title: data?.name ?? "Edit Organization",
      ctps,
      frameworks,
      membershipCount,
    };
  },
  component: CcoEditorPage,
});

function CcoEditorPage() {
  const { ccoId } = Route.useParams();
  const { ctps, frameworks, membershipCount } = Route.useLoaderData();
  const { data: cco, isLoading } = useQuery(cihCcoQueryOptions(ccoId));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!cco) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Organization not found.</div>
      </div>
    );
  }

  return (
    <CcoEditorForm
      ccoId={ccoId}
      cco={cco}
      ctps={ctps}
      frameworks={frameworks}
      membershipCount={membershipCount}
    />
  );
}

function CcoEditorForm({
  ccoId,
  cco,
  ctps,
  frameworks,
  membershipCount,
}: {
  ccoId: string;
  cco: CihCcoRow;
  ctps: CihCtpListRow[];
  frameworks: FrameworkListRow[];
  membershipCount: number;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");

  const form = useForm({
    defaultValues: {
      name: cco.name,
      slug: cco.slug ?? "",
      type: (cco.type as CCOType) ?? CCOType.CTO,
      subcategory: cco.subcategory ?? "",
      website: cco.website ?? "",
      logoUrl: cco.logoUrl ?? "",
      founder: cco.founder ?? "",
      founderUrl: cco.founderUrl ?? "",
      headquartersCountry: cco.headquartersCountry ?? "",
      foundedYear: cco.foundedYear != null ? String(cco.foundedYear) : "",
      accreditedBy: cco.accreditedBy ?? "",
      missionStatement: cco.missionStatement ?? "",
      differentiator: cco.differentiator ?? "",
      pullQuote: cco.pullQuote,
      pullQuoteAttribution: cco.pullQuoteAttribution,
      narrativeStats: cco.narrativeStats,
      hidePlatformStats: cco.hidePlatformStats,
      linkedin: cco.socialLinks?.linkedin ?? "",
      instagram: cco.socialLinks?.instagram ?? "",
      youtube: cco.socialLinks?.youtube ?? "",
      facebook: cco.socialLinks?.facebook ?? "",
      twitter: cco.socialLinks?.twitter ?? "",
      description: cco.description ?? "",
      body: cco.body != null ? JSON.stringify(cco.body) : "",
      faqs: (cco.faqs ?? []) as { question: string; answer: string }[],
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ccoQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: ccoQueryKeys.detail(ccoId),
    });
    queryClient.invalidateQueries({ queryKey: pageChangeQueryKeys.all });
  };

  /** After the draft is published or discarded, open the live row. */
  const openPublished = ({ publishedId }: { publishedId: string }): void => {
    queryClient.removeQueries({ queryKey: ccoQueryKeys.detail(ccoId) });
    invalidate();
    navigate({ to: "/admin/cih/ccos/$ccoId", params: { ccoId: publishedId } });
  };

  const getFormData = () => {
    const v = form.state.values;
    return {
      id: ccoId,
      name: v.name,
      slug: v.slug,
      type: v.type,
      subcategory: v.subcategory,
      website: v.website,
      logoUrl: v.logoUrl,
      description: v.description,
      founder: v.founder,
      founderUrl: v.founderUrl,
      headquartersCountry: v.headquartersCountry,
      foundedYear: v.foundedYear,
      accreditedBy: v.accreditedBy,
      missionStatement: v.missionStatement,
      differentiator: v.differentiator,
      pullQuote: v.pullQuote,
      pullQuoteAttribution: v.pullQuoteAttribution,
      narrativeStats: v.narrativeStats,
      hidePlatformStats: v.hidePlatformStats,
      socialLinks: {
        linkedin: v.linkedin,
        instagram: v.instagram,
        youtube: v.youtube,
        facebook: v.facebook,
        twitter: v.twitter,
      },
      body: v.body,
      faqs: v.faqs,
    };
  };

  const updateMutation = useMutation({
    mutationFn: updateCihCcoFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Organization saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await updateCihCcoFn({ data: getFormData() });
      return publishCcoFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Organization published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveCcoFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Organization archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createCcoDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/ccos/$ccoId",
        params: { ccoId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await updateCihCcoFn({ data: getFormData() });
      return promoteCcoDraftFn(data);
    },
    onSuccess: openPublished,
  });

  const discardDraftMutation = useMutation({
    mutationFn: discardDraftAsStaffFn,
    onSuccess: openPublished,
  });

  const restoreMutation = useMutation({
    mutationFn: restoreCcoFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Organization restored to draft.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveCcoFn,
    onSuccess: (result) => {
      if (result.publishedId !== ccoId) {
        openPublished(result);
        return;
      }
      invalidate();
      showSuccess("Organization approved and published.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnCcoToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Organization returned to draft.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCihCcoFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ccoQueryKeys.all });
      queryClient.removeQueries({ queryKey: ccoQueryKeys.detail(ccoId) });
      navigate({ to: "/admin/cih/ccos" });
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate({ data: getFormData() });
  };

  const handleDelete = (): void => {
    if (
      !window.confirm(
        `Delete "${cco.name}"? This cannot be undone. Only allowed for never-published drafts.`,
      )
    )
      return;
    deleteMutation.mutate({ data: { id: ccoId } });
  };

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    restoreMutation.isPending ||
    discardDraftMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    deleteMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    restoreMutation.error ??
    discardDraftMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    deleteMutation.error;

  const isDraft = cco.status === "draft";
  const isPublished = cco.status === "published";
  const isInReview = cco.status === "in_review";
  const isArchived = cco.status === "archived";
  const hasDraftParent = cco.draftOf != null;
  const canHardDelete = isDraft && !hasDraftParent;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">{cco.name}</h1>
              <ContentStatusBadge status={cco.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Organization editor — ID: {ccoId}</span>
              {cco.status === "published" && cco.slug && (
                <a
                  href={`/organizations/${cco.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && (
              <>
                {canHardDelete && (
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={anyPending}
                    loading={deleteMutation.isPending}
                    title="Delete this draft permanently"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                )}
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={anyPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
                {!hasDraftParent && (
                  <Button
                    variant="primary"
                    onClick={() =>
                      publishMutation.mutate({ data: { id: ccoId } })
                    }
                    disabled={anyPending}
                    loading={publishMutation.isPending}
                  >
                    Publish
                  </Button>
                )}
              </>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: ccoId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: ccoId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: ccoId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    const note = window.prompt("Feedback note for the author:");
                    if (note)
                      returnToDraftMutation.mutate({
                        data: { id: ccoId, note },
                      });
                  }}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() => restoreMutation.mutate({ data: { id: ccoId } })}
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="destructive-ghost"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      "Discard this draft? The live page stays as it is.",
                    )
                  ) {
                    discardDraftMutation.mutate({
                      data: { kind: "organization", draftId: ccoId },
                    });
                  }
                }}
                disabled={anyPending}
                loading={discardDraftMutation.isPending}
              >
                Discard draft
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  promoteMutation.mutate({ data: { draftId: ccoId } })
                }
                disabled={anyPending}
                loading={promoteMutation.isPending}
              >
                Promote to Live
              </Button>
            </div>
          </div>
        )}

        {hasDraftParent && (
          <div className="mb-4">
            <DraftReviewCard kind="organization" draftId={ccoId} />
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Basic Info
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="name">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-name" className="mb-1">
                      Name
                    </Label>
                    <Input
                      id="cco-name"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="slug">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-slug" className="mb-1">
                      Slug
                    </Label>
                    <Input
                      id="cco-slug"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                    {field.state.value !== (cco.slug ?? "") && (
                      <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                        <span>
                          Changing the slug will change the public URL. Existing
                          links will break — no redirect handling yet.
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </form.Field>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <form.Field name="type">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-type" className="mb-1">
                      Type
                    </Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(val) => {
                        const next = val as CCOType;
                        if (next === field.state.value) return;
                        const ok = window.confirm(
                          `Change type from ${field.state.value.toUpperCase()} to ${next.toUpperCase()}? This affects how the organization is categorized across the site.`,
                        );
                        if (!ok) return;
                        field.handleChange(next);
                      }}
                    >
                      <SelectTrigger id="cco-type" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CCOType.CTO}>CTO</SelectItem>
                        <SelectItem value={CCOType.PAO}>PAO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              <form.Field name="subcategory">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-subcategory" className="mb-1">
                      Subcategory{" "}
                      <span className="font-normal text-gray-400">
                        (optional)
                      </span>
                    </Label>
                    <Input
                      id="cco-subcategory"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="e.g., Executive Coaching"
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <form.Field name="website">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-website" className="mb-1">
                      Website
                    </Label>
                    <Input
                      id="cco-website"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="logoUrl">
                {(field) => (
                  <ImageUploadField
                    contentType="ccos"
                    contentId={ccoId}
                    purpose="logo"
                    currentUrl={field.state.value || null}
                    onUpload={field.handleChange}
                    onRemove={() => field.handleChange("")}
                    label="Logo"
                    recommendedDimensions="400x400"
                  />
                )}
              </form.Field>
            </div>
          </Card>

          {/* Organization Details */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Organization Details
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="founder">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-founder" className="mb-1">
                      Founder
                    </Label>
                    <Input
                      id="cco-founder"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="founderUrl">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-founder-url" className="mb-1">
                      Founder URL
                    </Label>
                    <Input
                      id="cco-founder-url"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://..."
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <form.Field name="headquartersCountry">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-hq" className="mb-1">
                      HQ Country
                    </Label>
                    <Input
                      id="cco-hq"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="foundedYear">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-year" className="mb-1">
                      Founded Year
                    </Label>
                    <Input
                      id="cco-year"
                      type="number"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="e.g., 1995"
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="accreditedBy">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-accredited" className="mb-1">
                      Accredited By
                    </Label>
                    <Input
                      id="cco-accredited"
                      type="text"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <form.Field name="missionStatement">
              {(field) => (
                <div className="mt-4">
                  <Label htmlFor="cco-mission" className="mb-1">
                    Mission Statement
                  </Label>
                  <Textarea
                    id="cco-mission"
                    rows={3}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="differentiator">
              {(field) => (
                <div className="mt-4">
                  <Label htmlFor="cco-diff" className="mb-1">
                    Differentiator
                  </Label>
                  <Textarea
                    id="cco-diff"
                    rows={3}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
          </Card>

          {/* Narrative (dark band) */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Narrative & Stats
            </h2>
            <form.Field name="pullQuote">
              {(field) => (
                <div>
                  <Label htmlFor="cco-pull-quote" className="mb-1">
                    Pull quote (rendered in the dark § 02 band)
                  </Label>
                  <Textarea
                    id="cco-pull-quote"
                    rows={3}
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="One sentence that captures the organization's mission"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="pullQuoteAttribution">
              {(field) => (
                <div className="mt-4">
                  <Label htmlFor="cco-pull-quote-attr" className="mb-1">
                    Pull quote attribution
                  </Label>
                  <Input
                    id="cco-pull-quote-attr"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., ICF Mission Statement"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="narrativeStats" mode="array">
              {(field) => (
                <div className="mt-4">
                  <Label className="mb-1">Narrative stats (up to 4)</Label>
                  <p className="mb-2 text-xs text-gray-500">
                    Big numbers about the real-world organization (e.g., "140+"
                    / "Countries"). Render in the hero stat strip before
                    platform-derived stats.
                  </p>
                  {(field.state.value ?? []).map((_, i) => (
                    <div key={i} className="mb-2 flex gap-2">
                      <form.Field name={`narrativeStats[${i}].value`}>
                        {(f) => (
                          <Input
                            placeholder="140+"
                            value={f.state.value ?? ""}
                            onValueChange={f.handleChange}
                            className="w-32"
                          />
                        )}
                      </form.Field>
                      <form.Field name={`narrativeStats[${i}].label`}>
                        {(f) => (
                          <Input
                            placeholder="Countries"
                            value={f.state.value ?? ""}
                            onValueChange={f.handleChange}
                            className="flex-1"
                          />
                        )}
                      </form.Field>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => field.removeValue(i)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {(field.state.value?.length ?? 0) < 4 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => field.pushValue({ value: "", label: "" })}
                    >
                      + Add stat
                    </Button>
                  ) : null}
                </div>
              )}
            </form.Field>

            <form.Field name="hidePlatformStats">
              {(field) => (
                <div className="mt-4 flex items-center gap-2">
                  <input
                    id="cco-hide-platform-stats"
                    type="checkbox"
                    checked={!!field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                  />
                  <Label htmlFor="cco-hide-platform-stats">
                    Hide platform-derived stats (only show narrative stats
                    above)
                  </Label>
                </div>
              )}
            </form.Field>
          </Card>

          {/* Social Links */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Social Links
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="linkedin">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-linkedin" className="mb-1">
                      LinkedIn
                    </Label>
                    <Input
                      id="cco-linkedin"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="instagram">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-instagram" className="mb-1">
                      Instagram
                    </Label>
                    <Input
                      id="cco-instagram"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="youtube">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-youtube" className="mb-1">
                      YouTube
                    </Label>
                    <Input
                      id="cco-youtube"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="facebook">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-facebook" className="mb-1">
                      Facebook
                    </Label>
                    <Input
                      id="cco-facebook"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="twitter">
                {(field) => (
                  <div>
                    <Label htmlFor="cco-twitter" className="mb-1">
                      Twitter
                    </Label>
                    <Input
                      id="cco-twitter"
                      type="url"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="https://x.com/..."
                    />
                  </div>
                )}
              </form.Field>
            </div>
          </Card>

          {/* Description */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Description
            </h2>
            <form.Field name="description">
              {(field) => (
                <>
                  <Label htmlFor="cco-description" className="mb-1">
                    Description{" "}
                    <span className="font-normal text-gray-400">
                      (meta/SEO)
                    </span>
                  </Label>
                  <Textarea
                    id="cco-description"
                    rows={4}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </>
              )}
            </form.Field>
          </Card>

          {/* Body */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Body
            </h2>
            <form.Field name="body">
              {(field) => (
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </Card>

          {/* FAQs */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              FAQs
            </h2>
            <form.Field name="faqs">
              {(field) => (
                <FaqEditor
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </Card>

          {/* Training Programs reverse-lookup */}
          <ReverseLookupCard
            title="Training Programs"
            summary={`${ctps.length} training program${ctps.length === 1 ? "" : "s"} owned by this organization.`}
            items={ctps.map((ctp) => ({
              id: ctp.id,
              label: ctp.name,
              href: `/admin/cih/ctps/${ctp.id}`,
            }))}
            pointer="To change which CCO owns a CTP, open that CTP's editor."
          />

          {/* Frameworks reverse-lookup */}
          <ReverseLookupCard
            title="Frameworks"
            summary={`${frameworks.length} framework${frameworks.length === 1 ? "" : "s"} associated with this organization.`}
            items={frameworks.map((fw) => ({
              id: fw.id,
              label: fw.name,
              href: `/admin/cih/frameworks/${fw.id}`,
            }))}
            pointer="To change which CCO a framework is associated with, open that framework's editor."
          />

          <CcoAccessCards ccoId={ccoId} />

          <Card>
            <h2 className="text-sm font-medium tracking-wide text-gray-500 uppercase">
              Change history
            </h2>
            <PageChangeHistory ccoId={cco.draftOf ?? ccoId} />
          </Card>

          {/* Coach memberships reverse-lookup */}
          <ReverseLookupCard
            title="Coach Memberships"
            summary={`${membershipCount} coach${membershipCount === 1 ? "" : "es"} hold a membership in this organization.`}
            pointer="To add, remove, or verify memberships, open the Verifications page."
          >
            <Link
              to="/admin/verifications"
              search={{ tab: "cco", ccoId }}
              className="text-sm text-brand-600 hover:underline"
            >
              View on Verifications page
            </Link>
          </ReverseLookupCard>
        </div>

        {/* Sticky save for draft */}
        {isDraft && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={anyPending}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
